import useTheme, { appColors } from '@base/useTheme';
import { ModelBaseDiff, ModelLoadOptions, ModelRepresentation, RefName } from '@common/api';
import { Button } from '@components/button';
import { CheckboxField, TristateCheckboxField } from '@components/form';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { Modal } from '@components/modal/Modal';
import { useFoldersActions } from '@data/folder/folderActions';
import { modelApi } from '@data/rest/api';
import { useI18nContext } from '@i18n/i18n-react';
import { patch, Update } from '@use-gpu/state';
import { tail } from '@util/array';
import { handleApiErrorMessage } from '@util/message';
import { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';

type ModelImportOptions = Omit<ModelLoadOptions, 'dryRun'>;

type Step = 'input' | 'options' | 'preview' | 'result';

const STEPS: Step[] = ['input', 'options', 'preview', 'result'];

const SKIP_BACK_STEPS = new Set(['preview']);

export const ModelImportModal = () => {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const { refreshAllFolders } = useFoldersActions();

    const [loading, setLoading] = useState(false);
    const [filename, setFilename] = useState('');
    const [stack, setStack] = useState<Step[]>(['input']);
    const [modelData, setModelData] = useState<ModelRepresentation | null>(null);
    const [preview, setPreview] = useState('');
    const [importOptions, setImportOptions] = useState<ModelImportOptions>({
        clear: false,
        deleteEnabled: {
            tables: false,
            rows: false,
            columns: false,
            variables: false,
            cells: false,
        },
    });

    const handleDrop = useCallback(
        async (acceptedFiles: File[]) => {
            try {
                const modelData = JSON.parse(await acceptedFiles[0]?.text());
                setModelData(modelData);
                setFilename(acceptedFiles[0].name);
            } catch (err) {
                await handleApiErrorMessage(err, LL.ERROR_IMPORTING_FILE());
            }
        },
        [LL, setModelData]
    );

    const { getRootProps, getInputProps, isDragAccept, inputRef } = useDropzone({
        onDrop: handleDrop,
        noClick: true,
    });

    const handleBrowseClick = () => {
        inputRef.current?.click();
    };

    const dropzoneStyle = useMemo(() => {
        return isDragAccept ? { backgroundColor: theme.colors.neutral.w100 } : {};
    }, [isDragAccept, theme]);

    const importOptionsAllDeleteEnabled = useMemo(() => {
        if (
            importOptions.deleteEnabled.tables &&
            importOptions.deleteEnabled.rows &&
            importOptions.deleteEnabled.columns &&
            importOptions.deleteEnabled.variables
        ) {
            return true;
        }
        if (
            !importOptions.deleteEnabled.tables &&
            !importOptions.deleteEnabled.rows &&
            !importOptions.deleteEnabled.columns &&
            !importOptions.deleteEnabled.variables
        ) {
            return false;
        }
        return null;
    }, [importOptions]);

    const handleNextStep = () => setStack((current) => [...current, STEPS[current.length]]);

    const handlePreviousStep = () =>
        setStack((current) => {
            const copy = current.slice();
            const last = copy.pop() as Step;
            if (SKIP_BACK_STEPS.has(last)) handlePreviousStep();
            return copy;
        });

    const load = async (options: Pick<ModelLoadOptions, 'dryRun'>, message?: string) => {
        if (modelData === null) {
            throw new Error('Import file data not set');
        }

        setLoading(true);
        const toastId = message && toast.loading(LL.LOADING_DATA);
        try {
            const data = await modelApi.load({ model: modelData, options: { ...importOptions, ...options } });
            toast.dismiss(toastId);
            if (message) toast.success(message);
            return data;
        } catch (error) {
            toast.dismiss(toastId);
            await handleApiErrorMessage(error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const handleOptionsUpdate = (update: Update<ModelImportOptions>) =>
        setImportOptions((current) => patch(current, update));

    const handleDryRun = async () => {
        const changes = await load({ dryRun: true });
        let preview = LL.IMPORT_PREVIEW() as string;
        for (const [type, diff] of Object.entries<ModelBaseDiff>(
            changes.diff as unknown as Record<string, ModelBaseDiff>
        )) {
            for (const [key, refs] of Object.entries<RefName[]>(diff as unknown as Record<string, RefName[]>)) {
                for (const r of refs) {
                    preview += `${key.toUpperCase()} ${type} "${r.name}"\n`;
                }
            }
        }

        setPreview(preview);
        handleNextStep();
    };

    const handleImport = async () => {
        await load({ dryRun: false }, LL.MODEL_IMPORT_SUCCESS());
        // TODO: send invalidation events for all created folders from BE
        await refreshAllFolders();
        setPreview((current) => current.replace(LL.IMPORT_PREVIEW(), LL.IMPORT_FINISHED()));
        handleNextStep();
    };

    const backButton = (
        <FlexRow $flexGrow={1}>
            <Button $type="quaternary" title={LL.BACK()} onClick={handlePreviousStep} disabled={loading} />
        </FlexRow>
    );
    let modalBody: JSX.Element | null = null;
    let modalFooter: JSX.Element | null = null;
    const current = tail(stack);
    switch (current) {
        case 'input': {
            modalBody = (
                <Modal.Body>
                    <FlexCol
                        $flexGrow={1}
                        $justify="center"
                        $gap={10}
                        $align="center"
                        {...getRootProps({ style: dropzoneStyle })}
                    >
                        <input type="file" ref={inputRef} style={{ display: 'none' }} {...getInputProps()} />
                        <span style={{ fontWeight: 700, fontSize: 16 }}>{LL.UPLOAD_MODEL_FILE()}</span>
                        <span style={{ color: filename ? appColors.ui.dark : appColors.neutral.w700, fontWeight: 400 }}>
                            {filename || LL.NO_FILE_SELECTED()}
                        </span>
                        <Button $type="tertiary" title={LL.BROWSE()} onClick={handleBrowseClick} />
                    </FlexCol>
                </Modal.Body>
            );
            modalFooter = (
                <Modal.Footer
                    doneType="primary"
                    doneLabel={LL.NEXT()}
                    done={handleNextStep}
                    loading={loading}
                    disabled={!modelData}
                />
            );
            break;
        }
        case 'options':
            modalBody = (
                <Modal.Body>
                    <FlexCol $gap={16}>
                        <TristateCheckboxField
                            size="small"
                            value={importOptionsAllDeleteEnabled}
                            label={LL.ALLOW_ALL()}
                            onChange={(selected) => {
                                handleOptionsUpdate({
                                    deleteEnabled: {
                                        tables: selected,
                                        rows: selected,
                                        columns: selected,
                                        variables: selected,
                                    },
                                });
                            }}
                        />

                        <FlexCol>
                            <span style={{ paddingLeft: 4, marginBottom: 8 }}>{LL.ALLOW_IMPORT_TO()}:</span>
                            <CheckboxField
                                size="small"
                                value={importOptions.deleteEnabled.tables}
                                label={LL.DELETE_TABLES()}
                                onChange={(tables) =>
                                    handleOptionsUpdate({
                                        deleteEnabled: {
                                            tables,
                                        },
                                    })
                                }
                            />
                            <CheckboxField
                                size="small"
                                value={importOptions.deleteEnabled.rows}
                                label={LL.DELETE_MEMBER_ROWS()}
                                onChange={(rows) =>
                                    handleOptionsUpdate({
                                        deleteEnabled: {
                                            rows,
                                        },
                                    })
                                }
                            />
                            <CheckboxField
                                size="small"
                                value={importOptions.deleteEnabled.columns}
                                label={LL.DELETE_COLUMNS()}
                                onChange={(columns) =>
                                    handleOptionsUpdate({
                                        deleteEnabled: {
                                            columns,
                                        },
                                    })
                                }
                            />
                            <CheckboxField
                                size="small"
                                value={importOptions.deleteEnabled.variables}
                                label={LL.DELETE_VARIABLES()}
                                onChange={(variables) => {
                                    handleOptionsUpdate({
                                        deleteEnabled: {
                                            variables,
                                        },
                                    });
                                }}
                            />
                        </FlexCol>

                        <CheckboxField
                            size="small"
                            value={importOptions.clear}
                            label={LL.DELETE_EVERYTHING()}
                            onChange={(clear) => {
                                handleOptionsUpdate({
                                    clear,
                                });
                            }}
                        />
                    </FlexCol>
                </Modal.Body>
            );
            modalFooter = (
                <Modal.Footer
                    extraBefore={backButton}
                    doneType="primary"
                    doneLabel={LL.PREVIEW()}
                    cancelLabel={LL.CLOSE()}
                    done={handleDryRun}
                    loading={loading}
                />
            );
            break;
        case 'preview':
        case 'result':
            modalBody = (
                <Modal.Body
                    style={{
                        padding: 10,
                        paddingBottom: 0,
                        textOverflow: 'clip',
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        marginTop: 20,
                        marginBottom: 20,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: appColors.neutral.w200,
                        borderStyle: 'solid',
                        color: current === 'result' ? appColors.ui.dark : appColors.neutral.w700,
                    }}
                >
                    <div style={{ marginBottom: 10 }}>{preview}</div>
                </Modal.Body>
            );
            modalFooter = (
                <Modal.Footer
                    extraBefore={backButton}
                    doneType="primary"
                    doneLabel={LL.IMPORT()}
                    done={handleImport}
                    loading={loading}
                    disabled={current === 'result'}
                />
            );
            break;
    }

    return (
        <Modal.Content width={425} height={400}>
            <Modal.Header title={LL.MODEL_IMPORT()} />
            {modalBody}
            {modalFooter}
        </Modal.Content>
    );
};
